// src/data/debarras.ts
//
// Page débarras / vide-maison — LOT 9 du plan SEO.
//
// PRÉALABLE DU PLAN : « ne lance ce lot que si Georges a confirmé que le
// service existe ». Le registre des faits ne dit rien du débarras. La page
// est donc construite avec l'EXISTENCE DU SERVICE comme donnée à valider :
// `serviceConfirme` vaut false tant que Gramme n'a pas dit, par écrit, que
// le débarras est proposé, à quelles conditions, et ce qui fait qu'il est
// gratuit ou payant. Le validateur refuse de publier tant que ce n'est pas
// le cas, et refuse toute affirmation sur la gratuité ou le prix qui ne
// vienne pas de Gramme.
//
// Ce que la page peut dire sans Gramme, avec une source officielle datée :
// le délai de la déclaration de succession, ce que les recyparcs acceptent
// et refusent, l'amiante. Ce qu'elle ne peut pas dire : que Gramme reprend
// le mobilier, qu'un débarras peut être gratuit, ce qu'il coûte.
import donnees from './debarras.json';
import type { SourceOfficielle } from './changement-adresse';

export const PAGE_DEBARRAS = '/vide-maison';

export interface ReglePratique {
  texte: string;
  /** Source officielle par id ; null pour un principe sans fait chiffré. */
  sourceId: string | null;
}

export interface SectionDebarras {
  id: string;
  titre: string;
  intro: string;
  regles: ReglePratique[];
}

/**
 * Ce que seule Gramme peut dire. Chaque champ à null est rendu comme un
 * marqueur « À VALIDER », et la page ne se publie pas tant qu'il en reste.
 */
export interface PolitiqueDebarras {
  /** Le service existe-t-il ? Rien ne se publie tant que ce n'est pas vrai. */
  serviceConfirme: boolean;
  /** Le débarras peut-il être gratuit, contre reprise du mobilier ? */
  gratuitePossible: boolean | null;
  /** Ce qui fait basculer du gratuit au payant, dans les mots de Gramme. */
  criteresGratuite: string[];
  /** Comment le prix se forme (au volume, au forfait, à la journée…). Pas un montant. */
  modeTarification: string | null;
  /** Ce que Gramme emporte, et ce qu'elle n'emporte pas. */
  emporte: string[];
  nEmportePas: string[];
  /** Où va le mobilier repris : revente, don, recyclage — les partenaires nommés par Gramme. */
  destinations: string[];
}

export interface PageDebarras {
  publiable: boolean;
  verifiePar: 'human' | null;
  dateVerification: string | null;
  aValider: string[];
}

interface Donnees {
  page: PageDebarras;
  politique: PolitiqueDebarras;
  sources: SourceOfficielle[];
  sections: SectionDebarras[];
  faq: Array<{ q: string; a: string; sourceId: string | null }>;
}

const D = donnees as unknown as Donnees;

export const PAGE: PageDebarras = D.page;
export const POLITIQUE: PolitiqueDebarras = D.politique;
export const SOURCES: readonly SourceOfficielle[] = D.sources;
export const SECTIONS: readonly SectionDebarras[] = D.sections;
export const FAQ = D.faq;

export function politiqueComplete(): boolean {
  const p = POLITIQUE;
  return p.serviceConfirme && p.gratuitePossible !== null && p.modeTarification !== null
    && p.emporte.length > 0 && p.nEmportePas.length > 0 && p.destinations.length > 0
    && (p.gratuitePossible === false || p.criteresGratuite.length > 0);
}

export function pageVerifiee(): boolean {
  return PAGE.verifiePar === 'human' && Boolean(PAGE.dateVerification) && politiqueComplete();
}

export interface ProblemeDebarras {
  ou: string;
  gravite: 'erreur' | 'avertissement';
  message: string;
}

const URL_OFFICIELLE = /^https:\/\/[^/]+\.be(\/|$)/;
const FAIT_CHIFFRE = /\b(un|deux|trois|quatre|cinq|six|\d+) (jours?|mois|m³|ans?)\b|\d+ ?€/i;
// Ce qu'on n'affirme pas à la place de Gramme.
const AFFIRMATION_GRATUITE = /(débarras|vide-maison|vide maison|enlèvement)\s+(est\s+)?gratuit/i;

export function validerDebarras(aujourdhui: Date = new Date()): ProblemeDebarras[] {
  const p: ProblemeDebarras[] = [];
  const limite = new Date(aujourdhui);
  limite.setUTCFullYear(limite.getUTCFullYear() - 1);
  const perimee = (iso: string | null) => Boolean(iso) && new Date(iso as string) < limite;
  const ids = new Set(SOURCES.map((s) => s.id));

  for (const s of SOURCES) {
    if (!URL_OFFICIELLE.test(s.url)) p.push({ ou: `sources.${s.id}`, gravite: 'erreur', message: `url « ${s.url} » n'est pas une URL https d'un domaine .be` });
    if (perimee(s.dateVerification)) p.push({ ou: `sources.${s.id}`, gravite: 'avertissement', message: `source vérifiée il y a plus de 12 mois (${s.dateVerification})` });
  }

  for (const s of SECTIONS) {
    for (const [i, r] of s.regles.entries()) {
      const ou = `sections.${s.id}[${i}]`;
      if (r.sourceId && !ids.has(r.sourceId)) p.push({ ou, gravite: 'erreur', message: `source inconnue : ${r.sourceId}` });
      if (!r.sourceId && FAIT_CHIFFRE.test(r.texte)) p.push({ ou, gravite: 'erreur', message: `règle chiffrée sans source : « ${r.texte.slice(0, 60)}… »` });
      if (AFFIRMATION_GRATUITE.test(r.texte)) p.push({ ou, gravite: 'erreur', message: `affirme la gratuité à la place de Gramme : « ${r.texte.slice(0, 60)}… »` });
    }
  }
  if (FAQ.length < 4 || FAQ.length > 8) p.push({ ou: 'faq', gravite: 'erreur', message: `${FAQ.length} question(s) — il en faut entre 4 et 8` });
  for (const [i, f] of FAQ.entries()) {
    if (f.sourceId && !ids.has(f.sourceId)) p.push({ ou: `faq[${i}]`, gravite: 'erreur', message: `source inconnue : ${f.sourceId}` });
    if (!f.sourceId && FAIT_CHIFFRE.test(f.a)) p.push({ ou: `faq[${i}]`, gravite: 'erreur', message: `réponse chiffrée sans source : « ${f.q} »` });
    if (AFFIRMATION_GRATUITE.test(f.a)) p.push({ ou: `faq[${i}]`, gravite: 'erreur', message: `affirme la gratuité à la place de Gramme : « ${f.q} »` });
  }

  if (!POLITIQUE.serviceConfirme) p.push({ ou: 'politique', gravite: 'avertissement', message: 'service non confirmé par Gramme — la page ne peut pas être publiée (préalable du lot 9)' });
  else if (!politiqueComplete()) p.push({ ou: 'politique', gravite: 'avertissement', message: 'politique incomplète (gratuité, tarification, emporte, n\'emporte pas, destinations) — marqueurs « À VALIDER » rendus' });
  if (PAGE.verifiePar !== 'human' && PAGE.verifiePar !== null) p.push({ ou: 'page', gravite: 'erreur', message: `verifiePar vaut « ${PAGE.verifiePar} » — attendu "human" ou null` });
  if (PAGE.publiable && !pageVerifiee()) p.push({ ou: 'page', gravite: 'erreur', message: 'publiable=true mais service non confirmé, politique incomplète ou page non relue' });
  if (PAGE.verifiePar === null) p.push({ ou: 'page', gravite: 'avertissement', message: 'page collectée mais non relue (verifiePar: null) — marqueur « À VALIDER » rendu' });
  if (perimee(PAGE.dateVerification)) p.push({ ou: 'page', gravite: 'avertissement', message: `page relue il y a plus de 12 mois (${PAGE.dateVerification})` });
  return p;
}
